<template>
  <div class="space-y-4">
    <!-- Credit banner -->
    <div v-if="creditTotal > 0" class="p-4 sm:p-5 rounded-lg bg-gray-600 text-gray-200">
      <div class="block uppercase tracking-wide text-gray-400 text-xs font-bold mb-1">Credit Owed</div>
      <div class="text-3xl font-bold">${{ creditTotal.toFixed(2) }}</div>
      <div class="text-sm text-gray-400 mt-1">
        {{ issueCount }} item{{ issueCount !== 1 ? 's' : '' }} with discrepancies
      </div>
    </div>

    <!-- Summary table -->
    <div class="rounded-lg overflow-hidden bg-gray-100">
      <div class="px-4 py-3 border-b border-gray-300">
        <div class="block uppercase tracking-wide text-gray-500 text-xs font-bold">Order Summary</div>
        <div class="mt-1.5 space-y-0.5">
          <div v-if="invoiceVendorName" class="text-sm font-semibold text-gray-700">{{ invoiceVendorName }}</div>
          <div class="flex flex-wrap items-center gap-x-3 gap-y-0.5">
            <span v-if="invoiceNumber" class="text-xs text-gray-600">Order #{{ invoiceNumber }}</span>
            <span v-if="invoiceOrderDate" class="text-xs text-gray-500">{{ invoiceOrderDate }}</span>
            <a
              v-if="firstScanImageUrl"
              :href="firstScanImageUrl"
              target="_blank"
              rel="noopener"
              class="text-xs text-blue-600 hover:text-blue-800 underline"
            >View scan</a>
          </div>
        </div>
      </div>

      <div
        v-for="item in summaryItems"
        :key="item.productId"
        @click="$emit('edit-item', item.productId)"
        class="px-4 py-3 border-b border-gray-200 last:border-0 cursor-pointer hover:bg-gray-200 active:bg-gray-200 transition-colors text-left"
        :class="item.hasIssue ? 'bg-red-50' : ''"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1 min-w-0">
            <div class="text-base font-semibold text-gray-800 leading-snug truncate">
              {{ item.name }}<span v-if="item.isExtra" class="font-normal text-gray-400 text-sm"> · extra</span>
            </div>
            <div v-if="item.productCode || item.packSize || (item.action && item.action !== '—')" class="text-xs text-gray-500 mt-0.5 truncate">
              <span v-if="item.productCode" class="font-mono">#{{ item.productCode }}</span><span v-if="item.productCode && (item.packSize || (item.action && item.action !== '—'))"> · </span><span v-if="item.packSize">{{ item.packSize }}</span><span v-if="item.packSize && item.action && item.action !== '—'"> · </span><span v-if="item.action && item.action !== '—'" :class="item.hasIssue ? 'text-red-500' : ''">{{ item.action }}</span>
            </div>
          </div>
          <div class="flex items-start gap-2 flex-shrink-0 pt-0.5">
            <div class="text-right">
              <div class="text-sm text-gray-700 tabular-nums whitespace-nowrap">
                <span class="text-gray-500 text-xs">Ord </span>{{ item.amountOrdered !== false ? item.amountOrdered : '—' }}
                <span class="text-gray-400 mx-1">·</span>
                <span class="text-gray-500 text-xs">Rcv </span><span :class="item.hasIssue ? 'text-red-600 font-semibold' : 'text-teal-700 font-semibold'">{{ item.amountReceived }}</span>
                <span v-if="item.amountReturned > 0" class="text-red-500 ml-1 text-xs">−{{ item.amountReturned }}</span>
              </div>
              <div v-if="item.lineTotal > 0 || item.unitCost > 0" class="text-xs text-gray-500 mt-0.5 tabular-nums">
                <span v-if="item.lineTotal > 0">${{ item.lineTotal.toFixed(2) }}</span><span v-else>${{ item.unitCost.toFixed(2) }}/u</span>
              </div>
            </div>
            <unicon name="angle-right" class="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="currentColor" />
          </div>
        </div>
      </div>

      <!-- Mispick / return items -->
      <div
        v-for="(item, idx) in mispickItems"
        :key="item.id"
        @click="$emit('edit-mispick', idx)"
        class="px-4 py-3 border-b border-gray-200 last:border-0 cursor-pointer hover:bg-gray-200 active:bg-gray-200 transition-colors text-left"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1 min-w-0">
            <div class="text-base font-semibold text-gray-800 leading-snug truncate">{{ item.description }}</div>
            <div class="text-xs mt-0.5" :class="item.disposition === 'returned' ? 'text-red-500' : 'text-gray-500'">
              {{ item.disposition === 'returned' ? 'Returning to vendor' : 'Accepted — not on order' }}
            </div>
          </div>
          <div class="flex items-start gap-2 flex-shrink-0 pt-0.5">
            <div class="text-sm text-gray-600 tabular-nums">
              Qty: <span class="font-semibold">{{ item.quantity }}</span>
            </div>
            <unicon name="angle-right" class="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" fill="currentColor" />
          </div>
        </div>
      </div>

      <!-- Invoice totals footer -->
      <div v-if="invoiceTax > 0 || invoiceFees > 0 || invoiceScanTotal !== null" class="border-t border-gray-300 px-4 py-3 space-y-1.5">
        <div v-if="itemsSubtotal > 0" class="flex justify-between text-sm text-gray-500">
          <span>Subtotal</span>
          <span class="tabular-nums">${{ itemsSubtotal.toFixed(2) }}</span>
        </div>
        <div v-if="invoiceTax > 0" class="flex justify-between text-sm text-gray-500">
          <span>Tax</span>
          <span class="tabular-nums">${{ invoiceTax.toFixed(2) }}</span>
        </div>
        <div v-if="invoiceFees > 0" class="flex justify-between text-sm text-gray-500">
          <span>Fees</span>
          <span class="tabular-nums">${{ invoiceFees.toFixed(2) }}</span>
        </div>
        <div v-if="invoiceScanTotal !== null" class="flex justify-between text-sm font-semibold text-gray-800 pt-1 border-t border-gray-200">
          <span>Invoice Total</span>
          <span class="tabular-nums">${{ invoiceScanTotal.toFixed(2) }}</span>
        </div>
      </div>
    </div>

    <!-- Add products toggle -->
    <div>
      <button
        @click="showAddProducts = !showAddProducts"
        class="w-full py-2 px-4 rounded-lg bg-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-300 active:bg-gray-300 transition-colors"
      >
        {{ showAddProducts ? 'Hide products' : '+ Add Products' }}
      </button>

      <div v-if="showAddProducts" class="mt-3 rounded-lg overflow-hidden bg-gray-100">
        <div class="px-4 py-3 border-b border-gray-300">
          <input
            v-model="productSearch"
            type="text"
            placeholder="Search products…"
            class="w-full appearance-none block bg-white text-gray-700 border border-gray-300 rounded py-2 px-3 text-sm focus:outline-none focus:border-gray-400"
          />
        </div>
        <div class="max-h-60 overflow-y-auto">
          <div
            v-for="prod in filteredExtraProducts"
            :key="prod.id"
            @click="addProduct(prod)"
            class="flex items-center justify-between px-4 py-3 border-b border-gray-200 last:border-0 cursor-pointer hover:bg-gray-200 text-gray-700 transition-colors"
          >
            <span class="text-sm font-medium">{{ prod.name }}</span>
            <unicon name="plus-circle" class="w-4 h-4 text-gray-400" fill="currentColor" />
          </div>
          <div v-if="filteredExtraProducts.length === 0" class="px-4 py-6 text-center text-sm text-gray-500">
            No products found
          </div>
        </div>
      </div>
    </div>

    <!-- Submit actions -->
    <div class="flex gap-2 pt-2">
      <button
        @click="$emit('back')"
        class="py-2 px-3 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300 active:bg-gray-300 transition-colors flex-shrink-0 flex items-center justify-center"
      >
        <unicon name="arrow-left" class="w-5 h-5" fill="currentColor" />
      </button>
      <button
        @click="$emit('submit')"
        class="flex-1 py-2 inline-flex items-center justify-center text-base sm:text-lg font-medium tracking-wide rounded-lg bg-gray-700 text-white hover:bg-gray-800 transition-colors shadow"
      >
        Submit & Complete
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { floatify } from '@/utils'

export default {
  name: 'ReceivingConfirmation',
  props: {
    invoiceId: { type: String, required: true },
    mispickItems: { type: Array, default: () => [] },
    scanResult: { type: Object, default: null }
  },
  data() {
    return { showAddProducts: false, productSearch: '' }
  },
  computed: {
    ...mapGetters('storeModule/products', ['productById', 'isProduct']),
    ...mapGetters('storeModule/invoices', ['invoiceById']),
    ...mapGetters('storeModule/vendors', ['vendorName']),
    ...mapGetters('storeModule', ['productsNotInInvoiceOrZeroedOutByInvoiceId']),

    invoice() {
      return this.invoiceById(this.invoiceId) || {}
    },

    invoicePositionMap() {
      const map = {}
      if (!this.scanResult || !this.scanResult.lineItems) return map

      this.scanResult.lineItems.forEach(item => {
        if (item.matchedProductId && item.lineIndex != null) {
          map[item.matchedProductId] = item.lineIndex
        }
      })

      // Fuzzy fallback: match unmatched scan lines to unmatched invoice products by word overlap
      const inv = this.invoice
      const unmatchedIds = Object.keys(inv).filter(k =>
        this.isProduct(k) && inv[k] && !inv[k].mispick && map[k] == null
      )
      if (unmatchedIds.length === 0) return map

      const sigWords = str =>
        str.toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter(w => w.length >= 3)

      this.scanResult.lineItems
        .filter(item => !item.matchedProductId && item.lineIndex != null && item.rawText)
        .forEach(scanItem => {
          const scanWords = sigWords(scanItem.rawText)
          if (scanWords.length === 0) return

          let bestId = null
          let bestScore = 0
          let bestHits = 0

          unmatchedIds.forEach(productId => {
            if (map[productId] != null) return
            const nameWords = sigWords((this.productById(productId) || {}).name || '')
            if (nameWords.length === 0) return
            const hits = nameWords.filter(w => scanWords.includes(w)).length
            const score = hits / nameWords.length
            if (hits >= 2 && score >= 0.6 && (score > bestScore || (score === bestScore && hits > bestHits))) {
              bestScore = score
              bestHits = hits
              bestId = productId
            }
          })

          if (bestId) {
            map[bestId] = scanItem.lineIndex
            unmatchedIds.splice(unmatchedIds.indexOf(bestId), 1)
          }
        })

      return map
    },

    summaryItems() {
      const posMap = this.invoicePositionMap
      return Object.keys(this.invoice)
        .filter(key => this.isProduct(key) && this.invoice[key] && !this.invoice[key].mispick)
        .map(key => {
          const item = this.invoice[key]
          const product = this.productById(key) || {}
          const unitCost = item.cost || 0
          const receivedQty = item.amountReceived || 0
          const packSize = product.unit || product.vendorPackSize || (product.qtyPerUnit > 1 ? `${product.qtyPerUnit} ct` : null)
          return {
            productId: key,
            name: product.name || item.name || key,
            productCode: product.code || null,
            action: item.action || '—',
            amountOrdered: item.amountOrdered,
            amountReceived: receivedQty,
            amountReturned: item.amountReturned || 0,
            unitCost,
            lineTotal: floatify(receivedQty * unitCost),
            packSize: packSize || null,
            isExtra: item.amountOrdered === false,
            hasIssue: item.action && item.action !== 'Received'
          }
        })
        .sort((a, b) => {
          const posA = posMap[a.productId] != null ? posMap[a.productId] : Infinity
          const posB = posMap[b.productId] != null ? posMap[b.productId] : Infinity
          if (posA !== posB) return posA - posB
          return a.name.localeCompare(b.name)
        })
    },

    issueCount() {
      return this.summaryItems.filter(i => i.hasIssue).length
    },

    creditTotal() {
      let total = 0
      this.summaryItems.forEach(item => {
        if (!item.hasIssue) return
        const inv = this.invoice[item.productId]
        if (!inv) return
        const ordered = inv.amountOrdered || 0
        const billed = inv.amountBilled || inv.amountReceived || 0
        if (ordered > billed) {
          total += floatify((ordered - billed) * (inv.cost || 0))
        }
      })
      return total
    },

    invoiceVendorName() {
      return this.invoice.vendorId ? this.vendorName(this.invoice.vendorId) : null
    },

    firstScanImageUrl() {
      if (this.invoice.scanImages && this.invoice.scanImages.length > 0) {
        return this.invoice.scanImages[0].downloadURL || null
      }
      return this.invoice.downloadURL || null
    },

    invoiceNumber() {
      return this.invoice.invoiceNumber || (this.scanResult && this.scanResult.header && this.scanResult.header.vendorInvoiceNumber) || null
    },

    invoiceOrderDate() {
      const ts = this.invoice.invoiceDate || this.invoice.deliveryDate || this.invoice.orderDate
      if (!ts) return null
      try {
        const d = ts.toDate ? ts.toDate() : new Date(ts)
        const month = d.toLocaleString('en-US', { month: 'short' })
        return `${month} ${d.getDate()}, ${d.getFullYear()}`
      } catch {
        return null
      }
    },

    invoiceTax() {
      return this.invoice.tax || 0
    },

    invoiceFees() {
      return this.invoice.fees || 0
    },

    invoiceScanTotal() {
      const header = this.invoice.autoReconciliation && this.invoice.autoReconciliation.header
      if (header && typeof header.totalAmountCents === 'number') {
        return header.totalAmountCents / 100
      }
      return null
    },

    itemsSubtotal() {
      return floatify(this.summaryItems.reduce((sum, item) => sum + (item.lineTotal || 0), 0))
    },

    extraProducts() {
      try {
        return this.productsNotInInvoiceOrZeroedOutByInvoiceId(this.invoiceId)
          .map(id => ({ id, name: (this.productById(id) || {}).name || id }))
          .sort((a, b) => a.name.localeCompare(b.name))
      } catch {
        return []
      }
    },

    filteredExtraProducts() {
      if (!this.productSearch) return this.extraProducts
      const q = this.productSearch.toLowerCase()
      return this.extraProducts.filter(p => p.name.toLowerCase().includes(q))
    }
  },
  watch: {
    creditTotal(val) {
      if (val > 0) this.updateItem({ id: this.invoiceId, data: { creditTotal: val } })
    }
  },
  methods: {
    ...mapActions('storeModule/invoices', ['addProductToInvoice', 'updateItem']),

    addProduct(prod) {
      this.addProductToInvoice({ invoiceId: this.invoiceId, productId: prod.id })
      this.showAddProducts = false
      this.productSearch = ''
      this.$emit('product-added', prod.id)
    }
  }
}
</script>
